import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { Chip, Consequence, MicroLabel, PassiveCard, ReadOnlyJson } from '@features/scoreSpec/styles';
import { outputContractOf, outputFieldsOf } from './duplicate';
import { PROSE_LEGACY_NOTE } from '@features/scoreSpec/proseContract';
import { ContractEditor } from './ContractEditor';
import { DeclareSchemaPanel } from './DeclareSchemaPanel';

// Everything the form is NOT changing, on screen anyway.
//
// The output contract is the reason this panel exists. `output_extraction` reads the model's
// answer with fixed JSONPaths, and those are validated against the CONTRACT, never against the
// prompt — so a new prompt that asks for a differently-shaped answer breaks extraction and nothing
// in the create call says so. It passes create, passes lock, and produces nulls forever.
//
// The form cannot enforce that coupling, which is exactly why it has to show it. A user who can
// see the fields their answer must contain will keep producing them; a user who cannot, will not.

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Divider = styled.div`
  height: 1px;
  margin: 24px 0;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

// `doc_types` has two shapes for the same key: plain names under `screen` and `trigger`, rule
// objects under `filter` / `related_filter`. Both render as the type name — the per-type cap is
// not something a prompt edit can affect.
const docTypeLabels = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (entry && typeof entry === 'object' ? entry.doc_type : entry))
    .filter((name) => typeof name === 'string' && name !== '');
};

// Two presses to throw work away, one to start. Opening the editor risks nothing; closing it
// discards every shape edit, and a control that does that on a single press next to a control that
// does not is a control that eventually eats somebody's afternoon.
const ContractToggle = ({ contract }) => {
  const [confirming, setConfirming] = React.useState(false);

  if (!contract.isEditing) {
    return <SkeletonButton onClick={contract.toggleEditing}>The answer shape changes too</SkeletonButton>;
  }
  if (!contract.isDirty) {
    return <SkeletonButton onClick={contract.toggleEditing}>Leave the shape unchanged</SkeletonButton>;
  }
  if (confirming) {
    return (
      <Line>
        <SkeletonButton
          onClick={() => {
            setConfirming(false);
            contract.toggleEditing();
          }}
        >
          Discard the shape edits
        </SkeletonButton>
        <Body7 isLowContrast>or</Body7>
        <SkeletonButton onClick={() => setConfirming(false)}>Keep editing</SkeletonButton>
      </Line>
    );
  }
  return <SkeletonButton onClick={() => setConfirming(true)}>Stop changing the shape</SkeletonButton>;
};

const Field = ({ label, children }) => (
  <Container top={16}>
    <MicroLabel>{label}</MicroLabel>
    <Container top={8}>{children}</Container>
  </Container>
);

export const ContextRail = ({ definition, contract: editable }) => {
  const spec = definition || {};
  const gen = spec.score_gen || {};
  const trigger = spec.trigger || {};
  const documents = (spec.document_set || {}).documents || {};
  const contract = outputContractOf(spec);
  const fields = outputFieldsOf(spec);

  const triggerTypes = docTypeLabels(trigger.doc_types);
  const documentTypes = docTypeLabels(documents.doc_types);
  const isEditing = !!editable && editable.isEditing;

  return (
    <PassiveCard aria-label="Carried across from the source spec">
      {/* The title lives on the step header above this card now. */}
      <Body7 isLowContrast>You are not changing any of this, but the new prompt still has to satisfy it.</Body7>

      <Field label="Model">
        <Body7>{gen.model || 'Not set on the source'}</Body7>
      </Field>

      <Field label={trigger.kind ? `Fires on ${trigger.kind}` : 'Trigger'}>
        <Line>
          {triggerTypes.length ? (
            triggerTypes.map((name) => <Chip key={name}>{name}</Chip>)
          ) : (
            <Body7 isLowContrast>
              {trigger.kind === 'pre_earnings_release'
                ? `${trigger.lead_time_days ?? '—'} days before the release`
                : 'No document types listed'}
            </Body7>
          )}
        </Line>
      </Field>

      <Field label={documents.kind ? `Reads documents.${documents.kind}` : 'Documents'}>
        <Line>
          {documentTypes.length ? (
            documentTypes.map((name) => <Chip key={name}>{name}</Chip>)
          ) : (
            <Body7 isLowContrast>
              {documents.kind === 'triggering_document'
                ? 'The document that fired the trigger'
                : 'No document types listed'}
            </Body7>
          )}
        </Line>
      </Field>

      <Divider />

      {isEditing ? (
        <ContractEditor contract={editable} />
      ) : (
        <div>
          <MicroLabel>The output contract</MicroLabel>
          <Container top={8}>
            {contract.kind === 'schema' || contract.kind === 'both' ? (
              <ReadOnlyJson>{JSON.stringify(contract.schema, null, 2)}</ReadOnlyJson>
            ) : (
              <ReadOnlyJson>{contract.prose}</ReadOnlyJson>
            )}
          </Container>

          {contract.kind === 'both' && (
            <Container top={8}>
              <Consequence $tone="warn">
                The source declares both an output_schema and a format_prompt. Create rejects that pair, so the copy
                keeps the schema — it is the one the engine enforces — and drops the prose.
              </Consequence>
            </Container>
          )}

          {fields.length > 0 && (
            <Field label="Fields extraction reads out of the answer">
              <Line>
                {fields.map((field) => (
                  <Chip key={`${field.kind}-${field.name}`} $tone={field.kind === 'score' ? 'good' : undefined}>
                    {`${field.name} ← ${field.jsonpath}`}
                  </Chip>
                ))}
              </Line>
            </Field>
          )}
        </div>
      )}

      {/* Only a schema has a shape to edit. A prose contract is shown and explained; changing it is a
          decision with a preview behind it, and the advanced editor is where the prose lives. */}
      {editable && editable.kind === 'schema' && (
        <Container top={16}>
          <ContractToggle contract={editable} />
        </Container>
      )}

      {editable && editable.kind === 'prose' && (
        <Container top={16}>
          <Consequence $tone="warn">{PROSE_LEGACY_NOTE}</Consequence>
          <Container top={16}>
            <DeclareSchemaPanel contract={editable} />
          </Container>
        </Container>
      )}

      <Container top={16}>
        <Consequence>
          {isEditing
            ? 'You are changing the shape as well, so the two halves are yours to keep in step: the prompt has to produce what these paths read. Nothing checks that — not create, not lock — and a mismatch shows up as nulls on every document.'
            : 'Your prompt can ask a different question. It still has to produce the same output fields — the ones shown here. Nothing in the create call checks that, and a mismatch does not surface until the spec is running.'}
        </Consequence>
      </Container>

      {isEditing &&
        editable.problems.advice.map((message) => (
          <Container top={8} key={message}>
            <Consequence $tone="warn">{message}</Consequence>
          </Container>
        ))}
    </PassiveCard>
  );
};
